import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import {
  Account,
  HubspotConfig,
  LifecycleStage,
  lifecycleStageDisplayLabel,
} from '@/client/accounts';
import { Actions, Subjects, SystemRoles } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { hubspotSchema } from '@/components/accounts/forms/validations';
import { HubspotProductLifecycle } from '@/components/accounts/hubspot/HubspotProductLifecycle';
import { FormDirtyTracker, FormikInput } from '@/components/form';
import { FormikChips } from '@/components/form/FormikChips';
import { useAccount, useUpdateAccounts } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import AppChip from '@/ui/chip/AppChip';
import { FlexContainer } from '@/ui/styled-ui';
import { AxiosError } from 'axios';
import { Field, Form, Formik } from 'formik';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FormEvent } from 'primereact/ts-helpers';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { AccountHeader } from './AccountHeader';

const StyledLink = styled.a`
  font-size: var(--small-font-size);
  font-weight: 500;
  line-height: 150%;
  text-decoration: none;
  color: var(--black-main);
  cursor: pointer;
  padding-bottom: var(--semi-xsmall-padding);
  border-bottom: 2px solid var(--gray-buttom-border);
  transition: border-bottom 0.2s ease-in-out;

  &:hover {
    border-bottom: 2px solid var(--red-dark);
  }
`;

const StyledConfigHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-top: var(--default-padding);
  margin-bottom: var(--xsmall-padding);
`;

const StyledConfigSubtitle = styled.p`
  font-size: var(--small-font-size);
  margin: 0 0 var(--default-padding);
`;

const StyledConfigCard = styled.div`
  border-radius: var(--semi-medium-border-radius);
  background: var(--beige-main);
  padding: var(--default-padding);
  display: flex;
  gap: var(--default-padding);
`;

const StyledConfigFields = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  gap: var(--medium-padding);

  label {
    font-size: var(--small-font-size);
    font-weight: 600;
  }
`;

const StyledMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
  font-size: var(--small-font-size);
`;

const StyledMetaLabel = styled.span`
  font-weight: 600;
  white-space: nowrap;
`;

const getPathItems = (
  account: Account,
  currentAccount: Account,
  t: TranslationFunctionType,
): MenuItem[] => {
  const items: MenuItem[] = [
    {
      label: currentAccount?.name,
      url: RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](currentAccount?.id),
      template: AppBreadCrumbTemplate,
    },
  ];

  if (currentAccount?.isSystem && account) {
    items.push(
      {
        label: t('accounts'),
        url: RedirectPaths[RedirectPathsEnum.ACCOUNTS](),
        template: AppBreadCrumbTemplate,
      },
      {
        label: account?.name,
        url: !currentAccount?.isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
          : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
        className: 'active',
        template: AppBreadCrumbTemplate,
      },
    );
  }

  items.push(
    {
      label: t('account.internal'),
      url: RedirectPaths[RedirectPathsEnum.ACCOUNTS_INTERNAL](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('hubspot'),
      url: RedirectPaths[RedirectPathsEnum.ACCOUNTS_INTERNAL_HUBSPOT](
        account?.id,
      ),
      template: AppBreadCrumbTemplate,
    },
  );

  return items;
};

export const AccountHubspot: React.FC = () => {
  const { t } = useTranslation();
  const { id: accountId } = useParams();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const {
    account,
    isLoading,
    refetch: accountRefetch,
  } = useAccount({ accountId });

  const toast = useToast();

  const [isDirty, setIsDirty] = useState(false);
  const submitRef = useRef<(() => Promise<boolean | void>) | null>(null);

  const [initialValues, setInitialValues] = useState<HubspotConfig>({
    hubspotId: '',
    hubspotDomains: [],
  });

  useEffect(() => {
    const source = account ?? (!accountId ? currentAccount : null);
    if (!source) return;

    setInitialValues({
      hubspotId: source.hubspot?.id,
      hubspotDomains: [
        source.hubspot?.primaryDomain || '',
        ...(source.hubspot?.additionalDomains || []),
      ].filter(Boolean),
    });
  }, [account, currentAccount]);

  const updateAccount = useUpdateAccounts();

  const handleUpdateHubspot = async (data: HubspotConfig) => {
    if (!accountId || !data) return;
    try {
      await updateAccount.update({
        updates: {
          hubspotId: data.hubspotId,
          hubspotPrimaryDomain: data?.hubspotDomains?.length
            ? data.hubspotDomains[0]
            : undefined,
          hubspotAdditionalDomains: data?.hubspotDomains?.slice(1),
        },
        accountId,
      });

      await accountRefetch();

      toast?.success(t('toast.success'), t('hubspot.account.updated'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const hubspotData = account?.hubspot;
  const companyOwnerName = hubspotData?.companyOwner
    ? `${hubspotData.companyOwner.firstName || ''} ${
        hubspotData.companyOwner.lastName || ''
      }`.trim()
    : undefined;
  const lifecycleStageLabel = hubspotData?.lifeCycleStage
    ? lifecycleStageDisplayLabel[hubspotData.lifeCycleStage]
    : undefined;
  const isCustomerLifecycle =
    hubspotData?.lifeCycleStage === LifecycleStage.CUSTOMER;

  const { cannot } = usePermission();

  const isSystemAccountRestricted =
    account?.isSystem && currentUser?.role.code !== SystemRoles.DEVELOPER;

  const isReadOnly =
    isSystemAccountRestricted ||
    cannot(Actions.UPDATE, Subjects.ACCOUNT_INTERNAL_TAB);

  return (
    <>
      {isLoading ? (
        <FlexContainer direction="column" className="mt-5">
          <ProgressSpinner />
        </FlexContainer>
      ) : (
        <>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleUpdateHubspot}
            validationSchema={hubspotSchema(t)}
          >
            {({ validateForm, setFieldValue }) => (
              <>
                {account && currentAccount && (
                  <>
                    <AppBreadCrumb
                      model={getPathItems(account, currentAccount, t)}
                    />
                    <AccountHeader
                      selectedAccount={account}
                      isDirty={isDirty}
                      onSave={
                        !isReadOnly
                          ? async () => submitRef.current?.()
                          : undefined
                      }
                    />
                  </>
                )}

                <Form className="w-full">
                  <FormDirtyTracker
                    onDirtyChange={setIsDirty}
                    submitRef={submitRef}
                  />
                  <StyledConfigHeader>
                    <h3 className="m-0">{t('hubspot.config')}</h3>
                    {hubspotData?.url && (
                      <StyledLink
                        href={hubspotData.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('hubspot.goTo')}
                      </StyledLink>
                    )}
                  </StyledConfigHeader>
                  <StyledConfigSubtitle>
                    {t('hubspot.config.description')}
                  </StyledConfigSubtitle>

                  <StyledConfigCard>
                    <StyledConfigFields>
                      <div className="field flex-1 mb-0">
                        <Field
                          id="hubspotId"
                          name="hubspotId"
                          label={t('hubspot.companyId')}
                          className="w-full white"
                          required
                          component={FormikInput}
                          disabled={isReadOnly}
                        />
                      </div>
                      <div className="field flex-1 mb-0">
                        <Field
                          id="hubspotDomains"
                          name="hubspotDomains"
                          label={t('hubspot.domains')}
                          className="w-full white"
                          component={FormikChips}
                          placeholder={t('hubspot.domains')}
                          required
                          maxSelectedLabels={1}
                          onChange={(event: FormEvent) => {
                            setFieldValue(
                              'hubspotDomains',
                              event.value?.map(
                                (domain: string) =>
                                  domain
                                    .replace(/https?:\/\//g, '')
                                    .split('/')[0],
                              ),
                            );

                            setTimeout(async () => {
                              await validateForm();
                            }, 0);
                          }}
                          disabled={isReadOnly}
                        />
                      </div>
                      <FlexContainer
                        direction="column"
                        gap="var(--medium-padding)"
                        width="auto"
                        align="flex-start"
                        className="flex-1"
                      >
                        {companyOwnerName && (
                          <StyledMetaRow>
                            <StyledMetaLabel>
                              {t('hubspot.companyOwner')}:
                            </StyledMetaLabel>
                            {companyOwnerName}
                          </StyledMetaRow>
                        )}
                        {lifecycleStageLabel && (
                          <StyledMetaRow>
                            <StyledMetaLabel>
                              {t('hubspot.lifeCycleStage')}:
                            </StyledMetaLabel>
                            <AppChip
                              label={lifecycleStageLabel}
                              type={
                                isCustomerLifecycle ? 'primary' : 'secondary'
                              }
                              size="s"
                            />
                          </StyledMetaRow>
                        )}
                      </FlexContainer>
                    </StyledConfigFields>
                  </StyledConfigCard>

                  <HubspotProductLifecycle hubspot={hubspotData} />
                </Form>
              </>
            )}
          </Formik>
        </>
      )}
    </>
  );
};
